import {
  AnimationType,
  IconType,
} from '@conversed/shared/src/central/types/webclient-config.type';
import type React from 'react';
import { useContext, useEffect, useMemo } from 'react';
import { ConfigContext } from '../context/ConfigContext';
import { useChatStatus } from '../helpers/useChatStatus';
import { useTimer } from '../helpers/useTimer';
import analyticsService from '../services/analyticsService';
import type { BubbleProps } from '../types/Bubble';

const getIconDataByWindowType = (
  chatIcon:
    | string
    | {
        open: string;
        closed: string | { type: string; path: string; animation?: string };
      },
  windowType: string,
  windowToggle: boolean,
) => {
  if (typeof chatIcon === 'string') {
    return {
      icon: chatIcon,
      type: IconType.STATIC,
      animation: AnimationType.NONE,
    };
  }

  if (windowToggle && windowType === 'open') {
    return {
      icon: chatIcon.open,
      type: IconType.STATIC,
      animation: AnimationType.NONE,
    };
  }

  if (typeof chatIcon.closed === 'object') {
    return {
      icon: chatIcon.closed?.path || '',
      type: chatIcon.closed?.type || IconType.STATIC,
      animation: chatIcon.closed?.animation || AnimationType.NONE,
    };
  }

  return {
    icon: chatIcon.closed,
    type: IconType.STATIC,
    animation: AnimationType.NONE,
  };
};

const Bubble: React.FC<BubbleProps> = ({
  clickHandler,
  windowType,
  windowToggle,
  changeChatActivityStatus,
  flowState,
}) => {
  const {
    bubbleProps: { dim, icon: chatIcon },
    configId,
    scheme,
  } = useContext(ConfigContext);

  const iconData = useMemo(
    () => getIconDataByWindowType(chatIcon, windowType, windowToggle),
    [windowType, windowToggle, chatIcon],
  );

  const { setSendChatStatus } = useChatStatus();
  // Only tick the counter when the bot is in the Handover block — other block
  // types (e.g. LLM) use their own configurable idle timeout via idleOptions.
  const { setWindowToggle, count } = useTimer(
    windowToggle,
    flowState === 'handover',
  );
  const isMobileDevice = analyticsService.isMobileDevice();

  // biome-ignore lint/correctness/useExhaustiveDependencies: changing dependencies might affect functionality
  useEffect(() => {
    // The counter only ticks when flowState === 'handover' (enforced by useTimer),
    // so reaching 45 is only possible during an active Handover block.
    if (count === 45) {
      setSendChatStatus({ status: 'idle' }, configId);
      changeChatActivityStatus('idle');
      setWindowToggle(false);
    }
  }, [count, setWindowToggle, configId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: changing dependencies might affect functionality
  useEffect(() => {
    const statusByWindowToggle = windowToggle ? 'online' : 'offline';
    setSendChatStatus({ status: statusByWindowToggle }, configId);
    changeChatActivityStatus(statusByWindowToggle);
    setWindowToggle(windowToggle);
  }, [windowToggle, configId]);

  // Get animation class based on type and animation
  const getAnimationClass = () => {
    if (
      iconData.type === IconType.ANIMATION &&
      iconData.animation !== AnimationType.NONE
    ) {
      return `chat-bubble-animated chat-bubble-${iconData.animation}`;
    }
    return '';
  };

  // Get header color for pulse animation
  const headerColor = scheme?.header?.headerColor || '#0554e3';

  return (
    <div
      className={`chat-bubble-container ${isMobileDevice ? 'mobile' : ''}`}
      aria-hidden='true'
      onClick={(e) => {
        clickHandler(e);
      }}
      style={
        {
          // Pass header color as CSS custom property for pulse animation
          '--header-color': headerColor,
        } as React.CSSProperties
      }>
      {iconData.icon ? (
        <img
          className={`chat-bubble-icon ${getAnimationClass()} ${isMobileDevice ? 'mobile' : ''}`}
          width={dim}
          height={dim}
          src={iconData.icon}
          alt='chat-bubble'
        />
      ) : (
        <i
          className={`far fa-comment chat-bubble-icon ${getAnimationClass()} ${isMobileDevice ? 'mobile' : ''}`}
        />
      )}
    </div>
  );
};

export default Bubble;
